<template>
  <div>
    <div class="header lb-portal">
      <div class="logo-container">
        <a href="/">
          <img
            v-if="showDesktopLogo"
            data-test-header-logo-desktop
            class="header-logo-desktop"
            :src="options.desktopLogo.url"
            :alt="options.desktopLogo.alt"
          />
          <img
            v-if="showMobileLogo"
            data-test-header-logo-mobile
            class="header-logo-mobile"
            :src="options.mobileLogo.url"
            :alt="options.mobileLogo.alt"
          />
        </a>
      </div>
      <div class="buttons-container">
        <div v-if="showSearch" v-click-away="handleSearchClickAway" class="search-container">
          <searchfield
            ref="searchfield"
            data-test-header-search
            :is-open="showSearchfield"
            :placeholder="inputPlaceholder"
            :value="searchFieldValue"
            @enter="performSearch"
            @input="searchFieldInputChanged"
          />
          <icon-button
            data-test-header-search-button
            class="search-icon"
            icon="search"
            :text="options.searchLabel"
            :send-click-tracking-event="true"
            @custom-click="toggleSearchfield"
          />
        </div>
        <icon-button
          v-if="showBasket"
          data-test-header-basket
          icon="basket"
          :text="options.basketLabel"
          :basket-count="options.numberOfProposals"
          :send-click-tracking-event="true"
          @custom-click="onIconButtonClick(options.basketLink)"
        />
        <icon-button
          v-if="showMinSide"
          data-test-header-minside
          icon="lock"
          :text="options.minSideLabel"
          :alt-color="true"
          :send-click-tracking-event="true"
          @custom-click="onIconButtonClick(options.minSideLink)"
        />
        <icon-button
          data-test-header-menu-button
          :icon="menuIcon"
          :text="options.menuLabel"
          @custom-click="toggleMenu"
        />
      </div>
    </div>
    <cvi-header-menu
      v-if="showMenu"
      ref="menu"
      data-test-header-menu
      :menu-links="options.menuLinks"
      :logged-in-links="options.loggedInMenuLinks"
      :is-member-logged-in="options.isMemberLoggedIn"
      :field-placeholder="inputPlaceholder"
      :input-field-value="searchFieldValue"
      :min-side-label="options.minSideLabel"
      :min-side-link="options.minSideLink"
      :logout-label="options.logoutLabel"
      :logout-link="options.logoutLink"
      @input="searchFieldInputChanged"
      @search="performSearch"
      @overlay-click="closeMenu"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { HeaderOptions, HeaderData } from './types/CviHeader';
import IconButton from './IconButton.vue';
import CviHeaderMenu from './CviHeaderMenu.vue';
import Searchfield from './Searchfield.vue';
import clickAway from '@/directives/clickAway';
import type { Link } from '@/interfaces/Link';
import { defaultLink } from '@/interfaces/Link';
import Utility from '@/common/UtilityFunctions.js';
import type { Image } from '@/interfaces/Image';
import type { PropType } from 'vue';

export default defineComponent({
  name: 'CviHeader',
  components: {
    CviHeaderMenu,
    IconButton,
    Searchfield
  },
  directives: {
    // ts-ignore
    clickAway
  },
  props: {
    options: {
      type: Object as PropType<HeaderOptions>,
      default: () => ({
        desktopLogo: {
          url: 'https://dev-minside.lb.dk/-/media/logo/new-header/lb/logo-full-screen.svg',
          alt: 'Logo full screen',
          editorField:
            '<img src="https://dev-minside.lb.dk/-/media/logo/new-header/lb/logo-full-screen.svg?iar=0&amp;hash=1551A7D04D3BCA67E105B2228B793E06" alt="" />'
        },
        mobileLogo: {
          url: 'https://dev-minside.lb.dk/-/media/logo/new-header/lb/logo-full-screen.svg',
          alt: 'Logo full screen',
          editorField:
            '<img src="https://dev-minside.lb.dk/-/media/logo/new-header/lb/logo-full-screen.svg?iar=0&amp;hash=1551A7D04D3BCA67E105B2228B793E06" alt="" />'
        },
        logoLink: {
          url: 'https://local.lb.dk/',
          text: 'L&#230;rerstandens Brandforsikring',
          abstract: '',
          cssClass: '',
          isActive: false,
          isOpen: false,
          target: '',
          editorField: null
        },
        menuLinks: [
          {
            label: 'Bliv medlem',
            link: {
              url: 'https://dev-public.lb.dk/bliv-medlem',
              text: 'Bliv medlem',
              abstract: '',
              cssClass: '',
              isActive: false,
              isOpen: false,
              target: '',
              editorField: null
            },
            loggedInLink: null
          },
          {
            label: 'Forsikringer',
            link: {
              url: 'https://dev-public.lb.dk/forsikringer',
              text: 'Forsikringer',
              abstract: '',
              cssClass: '',
              isActive: false,
              isOpen: false,
              target: '',
              editorField: null
            },
            loggedInLink: null
          },
          {
            label: 'Anmeld skade',
            link: {
              url: 'https://dev-public.lb.dk/anmeld-skade',
              text: 'Anmeld skade',
              abstract: '',
              cssClass: '',
              isActive: false,
              isOpen: false,
              target: '',
              editorField: null
            },
            loggedInLink: {
              url: '/opret-skade',
              text: 'Opret skade',
              abstract: '',
              cssClass: '',
              isActive: false,
              isOpen: false,
              target: '',
              editorField: null
            }
          },
          {
            label: 'Kontakt os',
            link: {
              url: 'https://dev-public.lb.dk/kontakt',
              text: 'Kontakt',
              abstract: '',
              cssClass: '',
              isActive: false,
              isOpen: false,
              target: '',
              editorField: null
            },
            loggedInLink: null
          },
          {
            label: 'Undg&#229; skader',
            link: {
              url: 'https://dev-public.lb.dk/undgaa-skader',
              text: 'Undgaa skader',
              abstract: '',
              cssClass: '',
              isActive: false,
              isOpen: false,
              target: '',
              editorField: null
            },
            loggedInLink: null
          }
        ],
        loggedInMenuLinks: [
          {
            label: 'Mine forsikringer',
            link: {
              url: '/selvbetjening',
              text: 'Min side',
              abstract: '',
              cssClass: '',
              isActive: false,
              isOpen: false,
              target: '',
              editorField: null
            },
            anchor: '#policies'
          },
          {
            label: 'Mine skader',
            link: {
              url: '/selvbetjening',
              text: 'Min side',
              abstract: '',
              cssClass: '',
              isActive: false,
              isOpen: false,
              target: '',
              editorField: null
            },
            anchor: '#claims'
          },
          {
            label: 'Mine betalinger og regninger',
            link: {
              url: 'https://minside.lb.dk/dokumenter?documenttype=1136',
              text: 'Betaling af forsikring',
              abstract: '',
              cssClass: '',
              isActive: false,
              isOpen: false,
              target: '',
              editorField:
                '<a href="https://minside.lb.dk/dokumenter?documenttype=1136">Betaling af forsikring</a>'
            },
            anchor: ''
          },
          {
            label: 'Mine oplysninger',
            link: {
              url: '/selvbetjening',
              text: 'Min side',
              abstract: '',
              cssClass: '',
              isActive: false,
              isOpen: false,
              target: '',
              editorField: null
            },
            anchor: '#memberInfo'
          },
          {
            label: 'Mine dokumenter',
            link: {
              url: '/selvbetjening',
              text: 'Min side',
              abstract: '',
              cssClass: '',
              isActive: false,
              isOpen: false,
              target: '',
              editorField: null
            },
            anchor: '#documents'
          },
          {
            label: 'Mine fuldmagter',
            link: {
              url: '/selvbetjening',
              text: 'Min side',
              abstract: '',
              cssClass: '',
              isActive: false,
              isOpen: false,
              target: '',
              editorField: null
            },
            anchor: '#powerOfAttorney'
          }
        ],
        basketLabel: 'Kurv',
        basketLink: {
          url: '/kurv',
          text: 'Kurv',
          abstract: '',
          cssClass: '',
          isActive: false,
          isOpen: false,
          target: '',
          editorField: null
        },
        minSideLabel: 'MinSide',
        minSideLink: {
          url: '/selvbetjening',
          text: 'Min side',
          abstract: '',
          cssClass: '',
          isActive: false,
          isOpen: false,
          target: '',
          editorField: null
        },

        searchLabel: 'S&#248;g',
        menuLabel: 'Menu',
        searchPageLink: {
          url: 'https://dev-public.lb.dk/search',
          text: 'Search',
          abstract: '',
          cssClass: '',
          isActive: false,
          isOpen: false,
          target: '',
          editorField: null
        },
        searchFieldPlaceholder: 'Hvad leder du efter?',
        isMemberLoggedIn: true,
        logoutLabel: 'Log af',
        logoutLink: {
          url: '/logout',
          text: 'Log ud',
          abstract: '',
          cssClass: '',
          isActive: false,
          isOpen: false,
          target: '',
          editorField: null
        },
        numberOfProposals: 0
      })
    }
  },
  data(): HeaderData {
    return {
      showMenu: false,
      showSearchfield: false,
      searchFieldValue: '',
      ignoreScrollEvents: false
    };
  },
  computed: {
    menuIcon(): string {
      return this.showMenu ? 'close' : 'menu';
    },
    inputPlaceholder(): string {
      return Utility.decodeHtml(this.options.searchFieldPlaceholder);
    },
    wheelEvent(): string {
      return 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';
    },
    showDesktopLogo(): boolean {
      return this.urlExists(this.options.desktopLogo);
    },
    showMobileLogo(): boolean {
      return this.urlExists(this.options.mobileLogo);
    },
    showSearch(): boolean {
      return this.urlExists(this.options.searchPageLink);
    },
    showBasket(): boolean {
      return this.urlExists(this.options.basketLink);
    },
    showMinSide(): boolean {
      return this.urlExists(this.options.minSideLink);
    }
  },
  mounted() {
    // modern Chrome requires non passive event listener when adding event
    const supportsPassive = this.detectPassiveEventListenerSupport();
    const wheelOpt = supportsPassive ? { passive: false } : false;

    window.addEventListener('DOMMouseScroll', this.preventDefaultIfMenuOpen, false); // older FF
    window.addEventListener(this.wheelEvent, this.preventDefaultIfMenuOpen, wheelOpt); // modern desktop
    window.addEventListener('touchmove', this.preventDefaultIfMenuOpen, wheelOpt); // mobile
    window.addEventListener('keydown', this.preventDefaultForScrollKeys, false);
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener('DOMMouseScroll', this.preventDefaultIfMenuOpen, false);
    window.removeEventListener(this.wheelEvent, this.preventDefaultIfMenuOpen);
    window.removeEventListener('touchmove', this.preventDefaultIfMenuOpen);
    window.removeEventListener('keydown', this.preventDefaultForScrollKeys, false);
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    urlExists(link: Link | Image) {
      return link && link.url !== '';
    },
    handleScroll(): void {
      if (this.ignoreScrollEvents) {
        this.ignoreScrollEvents = false;
        return;
      }

      if (this.showMenu && !this.ignoreScrollEvents) this.closeMenu();
    },
    preventDefaultIfMenuOpen(event: Event): void {
      const element = document.getElementById('header-menu-nav');
      const navHasScrollbar = Utility.hasScrollbar(element);
      const elementIsNavOrNavDescendant =
        event.target === element || element?.contains(event.target as Node);

      if (this.showMenu && (!elementIsNavOrNavDescendant || !navHasScrollbar))
        event.preventDefault();
    },
    preventDefaultForScrollKeys(e: KeyboardEvent): void {
      const scrollKeys = [
        ' ',
        'ArrowLeft',
        'ArrowRight',
        'ArrowDown',
        'ArrowUp',
        'End',
        'Home',
        'PageDown',
        'PageUp'
      ];

      if (scrollKeys.includes(e.key)) {
        this.preventDefaultIfMenuOpen(e);
      }
    },
    setOverflow(hideOverflow: boolean): void {
      if (hideOverflow) document.body.classList.add('no-scroll');
      else document.body.classList.remove('no-scroll');
    },
    searchFieldInputChanged(event: Event): void {
      const target = event.target as HTMLInputElement;
      const inputText = target.value;
      this.searchFieldValue = inputText;
    },
    closeMenu(): void {
      console.log('closeMenu');
      this.showMenu = false;
      this.setOverflow(this.showMenu);
    },
    detectPassiveEventListenerSupport(): boolean {
      let supportsPassive = false;
      try {
        window.addEventListener(
          'detectPassive',
          () => {},
          Object.defineProperty({}, 'passive', {
            get() {
              supportsPassive = true;
              return undefined;
            }
          })
        );
        return supportsPassive;
      } catch (e) {
        return false;
      }
    },
    toggleMenu(): void {
      const showMenu = !this.showMenu;

      this.$store.dispatch('Events/register', {
        event: showMenu ? 'cvi-header-click-open' : 'cvi-header-click-close',
        id: 'cviHeader',
        eventLabel: this.options.menuLabel
      });

      if (showMenu && document.documentElement.scrollTop > 0) {
        this.ignoreScrollEvents = true;

        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      }

      this.showMenu = showMenu;
      this.setOverflow(this.showMenu);
    },
    performSearch(): void {
      if (!this.searchFieldValue) {
        this.focusSearchfield();
        return;
      }

      let currentLocation = location.pathname;
      const searchPageUrl = this.options.searchPageLink.url;
      const redirectLink = `${searchPageUrl}?q=${this.searchFieldValue}`;

      if (currentLocation !== searchPageUrl) window.location.href = redirectLink;
      else {
        this.$store.dispatch('search/performSearch', this.searchFieldValue);
        window.history.pushState({ path: redirectLink }, '', redirectLink);
      }
    },
    toggleSearchfield(): void {
      if (this.showSearchfield) {
        this.performSearch();
      } else {
        this.showSearchfield = true;
        this.focusSearchfield();
      }
    },
    handleSearchClickAway(): void {
      this.showSearchfield = false;
    },
    focusSearchfield(): void {
      const searchfieldRef = this.$refs.searchfield;
      if (searchfieldRef) {
        (searchfieldRef as HTMLInputElement & { focusInput: () => void }).focusInput();
      }
    },
    onIconButtonClick(link: Link): void {
      window.location.href = link.url;
    }
  }
});
</script>

<style lang="scss" scoped>
@import 'src/styles/all.scss';
@import 'node_modules/foundation-sites/scss/util/util';

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #ffffff;
  border-bottom: 1px solid $medium-gray;
  box-sizing: border-box;

  .buttons-container {
    display: inline-flex;
    align-items: flex-start;
  }

  img {
    min-width: rem-calc(80px);
  }
}

@include breakpoint(medium up) {
  .logo-container {
    .header-logo-mobile {
      display: none;
    }

    .header-logo-desktop {
      height: rem-calc(75px);
    }
  }

  .header {
    height: rem-calc(124px);
    padding: rem-calc(24px) rem-calc(56px);

    .search-container {
      display: flex;
      gap: rem-calc(12px);
    }
  }

  .buttons-container {
    gap: rem-calc(32px);
  }
}

@include breakpoint(medium down) {
  .search-icon {
    display: none;
  }

  .search-container {
    display: none;
  }
}

@include breakpoint(small only) {
  .logo-container {
    max-width: rem-calc(128px);

    .header-logo-mobile {
      height: rem-calc(40px);
      width: auto;
    }

    .header-logo-desktop {
      display: none;
    }
  }

  .header {
    height: rem-calc(80px);
    padding: rem-calc(12px) rem-calc(24px);
  }

  .buttons-container {
    gap: rem-calc(16px);
  }
}
</style>
../../../../directives/clickAway
